import React, {useEffect, useMemo, useRef, useState} from "react";
import Message from "../Message/Message";
import SendMessageBar from "../SendMessageBar/SendMessageBar";
import RainbowSDKService from "../services/RainbowSDKService";
import Config from "../config/config.json";
import { useTranslation } from 'react-i18next';
import Typing from "../Typing/Typing";
import Information from "../Information/Information";
import "./WelcomeChat.css";
import {OpenUrlAction, SubmitAction} from "adaptivecards";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import {getKnownContact} from "../services/UtilsService";
import {
    BubbleServiceEvents,
    ConversationEvents,
    ConversationServiceEvents,
    Message as MessageRB,
    RBEvent,
    User,
    UserNetworkServiceEvents,
    Bubble, Conversation, SendMessageData
} from "rainbow-web-sdk";


type WelcomeChatProps = {
    appState: any;
    setAppState: (state: any) => void;
    States: any;
    bubble?: Bubble;
    disabled?: boolean;
    handleReasonForChatChange: (event: any) => void;
    reasonForChat?: any;
    reloadReasonForChat?: (event: any) => void
}

type MessageReceivedDetail = {
    message: MessageRB,
    conversation: any
}

const WelcomeChat: React.FC<WelcomeChatProps> = (props) => {
    const [messages, setMessages] = useState<MessageRB[]>([]);
    const [mainConversation, setMainConversation] = useState<Conversation>();
    const [contacts, setContacts] = useState<User[]>([]);
    
    // Create a ref to hold the current mainConversation
    const mainConversationRef = useRef<Conversation | undefined>(undefined);
    const [typing, setTyping] = useState(false);
    const [disabled, setDisabled] = useState(props.disabled || false);
    const { t } = useTranslation();

    const [conversationRetrieved, setConversationRetrieved] = useState(false);
    const chatRef = useRef<HTMLDivElement>(null);

    // Update the ref whenever mainConversation changes
    useEffect(() => {
        mainConversationRef.current = mainConversation;
        mainConversationRef.current?.subscribe((event: RBEvent<ConversationEvents, any>) => {
            onNewParticipantStatus(event);
        }, ConversationEvents.ON_STATUS_CHANGED)
    }, [mainConversation]);

    useEffect(() => {
        const resizeHandler = () => forceUpdate();
        window.addEventListener("resize", resizeHandler);

        const sdk = RainbowSDKService.rainbowSDK;
        sdk.conversationService.subscribe((event: RBEvent<ConversationServiceEvents, any>) => {
            onMessageReceived(event);
        }, ConversationServiceEvents.ON_MESSAGE_ADDED_IN_CONVERSATION);

        sdk.userNetwork.subscribe((event: RBEvent<UserNetworkServiceEvents, any>) => {
            onInvitationReceived(event);
        }, UserNetworkServiceEvents.RAINBOW_ON_INVITATION_RECEIVED);

        sdk.bubbleService?.subscribe((event: RBEvent<BubbleServiceEvents, any>) => {
            switch(event.name) {
                case BubbleServiceEvents.ON_SERVICE_STARTED:
                    // Received when the bubble service is started and ready to be used
                    // (also fired after loss of network and reconnection).
                    break;
                case BubbleServiceEvents.ON_BUBBLE_CREATED:
                    // Received when a new bubble is created.
                    break;
                case BubbleServiceEvents.ON_BUBBLE_DELETED:
                    // Received when a bubble has been deleted by the owner.
                    break;
                case BubbleServiceEvents.ON_CONNECTED_USER_ROLE_UPDATED:
                    // Received when there's an update on the role of the connected user privilege.
                    // (owner, organizer, user)
                    break;
                case BubbleServiceEvents.ON_USER_INVITATION_RECEIVED:
                    // Received when the connected user has received an invitation to join a bubble.
                    break;
                case BubbleServiceEvents.ON_USER_INVITATION_DECLINED:
                    // Received when a user has declined the invitation to join the bubble
                    break;
                case BubbleServiceEvents.ON_USER_BUBBLE_LEFT:
                    // Received when a user has left the bubble of his own
                    console.log(event)
                    const { bubbleUser } = event.data;
                    if (bubbleUser.user.id === sdk.connectedUser?.id) {
                        props.setAppState(props.States.ChatArchived);
                    }
                    break;
                case BubbleServiceEvents.ON_USER_BUBBLE_REMOVED:
                    // Received when a user has been removed from the bubble by the owner or one of the organizers.
                    // Received when the bubble in which the user was a member has been deleted.
                    props.setAppState(props.States.ChatArchived);
                    break;
                default: break;
            }
        });

        return () => {
            window.removeEventListener("resize", resizeHandler);
        };
    }, []);

    useEffect(() => {
        if (props.appState === props.States.Authenticated) {
            getConversationMessages();
            getNetworkContacts();
        }
    }, [props.appState]);

    const forceUpdate = () => setDisabled((prev) => !prev);

    const scrollToBottom = (fromSelect = false, timeout = 100) => {
        if (chatRef.current) {
            setTimeout(() => {
                if (chatRef?.current) {
                    chatRef.current.scrollTop = chatRef.current.scrollHeight;
                }
            }, timeout);
        }
    };

    const isMessageToAdd = (message: any) => {
        if (message.side === "ADMIN" || message.urgency === "low") return false;
        if (
            (message.side === "R" && message.data === Config.chatService.botInitMessage) || (message.side === "L" &&
            Config.chatService.hiddenMessagePrefix.find((prefix) =>
                message.data.startsWith(prefix)
            ))
        )
            return false;
        return true;
    };

    const addRainbowMessageToList = (message: MessageRB) => {
        if (!isMessageToAdd(message)) return;
        if (!messages.find((m) => m.id === message.id)) {
            setMessages((prev) => [...prev, message]);
            scrollToBottom();
        }
    };

    const onMessageReceived = (event: any) => {
        console.log("Main conversation : ", mainConversationRef.current);
        console.log("message received : ", event);

        const {message, conversation } = event.data as MessageReceivedDetail;
        if (!message || !conversation) return;

        RainbowSDKService.markMessageFromConversationAsRead(conversation, message);
        const commands = message.data.trim().split(" ").filter(Boolean);
        const isMessageFromUs = message.from.id === RainbowSDKService.rainbowSDK.connectedUser?.id;

        if (isMessageFromUs &&
            (
                message.data.startsWith(t("no_message_in_conversation")) ||
                message.data.startsWith(t("continue_conversation_here"))
            )
        ) {
            console.log("We are the owner of these automatic messages, do not add it in the regular conversation");
        }
        else if ((mainConversationRef.current && conversation.id !== mainConversationRef.current.id) && !isMessageFromUs) {
            console.log("Wrong conversation, message certainly sent to user directly")
            conversation.sendMessage(t("no_message_in_conversation") + ` "${mainConversationRef.current.name}"`)
            RainbowSDKService.sendMessageToConversation(
                conversation,
                t("no_message_in_conversation") + ` "${mainConversationRef.current.name}"`
            );
            RainbowSDKService.sendMessageToConversation(
                mainConversationRef.current,
                t("continue_conversation_here") as string
            );
        } else if (Config.chatService.hiddenMessagePrefix.includes(commands[0])) {
            console.log("Admin command ignore adding the message to the conversation");
        } else {
            console.log("Add regular message in conversation");
            addContactToNetwork(message.from);
            addRainbowMessageToList(message);
        }
    };

    const onNewParticipantStatus = (event: any) => {
        const { status } = event.data;
        setTyping(status === "composing");
    };

    const onInvitationReceived = (event: any) => {
        const { id } = event.detail;
        RainbowSDKService.acceptInvitationReceived(id)
            .then(() => console.log("Invitation accepted"))
            .catch((e) => console.error("Invitation error", e));
    };

    const getConversationMessages = async ()=> {
        if (!conversationRetrieved) {
            setConversationRetrieved(true);
            if (props.bubble !== null){
                console.log("Bubble", props.bubble);
                const conversation = await RainbowSDKService.rainbowSDK.conversationService.getConversation(props.bubble?.jid);
                console.log("Conversation retrieved", conversation);
                if (!conversation) {
                    console.error("[WelcomeChat] Got no conversation");
                    return;
                }

                setMainConversation(conversation);
                const conversationWithHistory = await RainbowSDKService.getMessagesFromConversation(conversation)
                console.log("Conversation with history", conversationWithHistory);
                let filteredMessages = conversationWithHistory.filter(message => {
                    return isMessageToAdd(message);
                })

                if (filteredMessages.length > 0) {
                    setMessages(filteredMessages);
                } else {
                    RainbowSDKService.sendMessageToConversation(
                        conversation,
                        Config.chatService.botInitMessage
                    );
                }
            }
        }
    }

    const getNetworkContacts = async () => {
        if (contacts.length === 0) {
            const networkContacts = RainbowSDKService.getNetworkContacts()
            setContacts(networkContacts)
        }
    }

    const addContactToNetwork = (contact: any)=> {
        if(getKnownContact(contact) && !contacts.find(c =>c.dbId===contact.dbId)){
            RainbowSDKService.addToNetwork(contact.dbId).then(()=>getNetworkContacts()).catch(e=>{
                if(e.message.includes('joinContactInvitation')){

                }else{
                    throw e
                }
            })
        }
    }

    const handleAdaptiveCardActions = (action: SubmitAction | OpenUrlAction) => {
        if (action instanceof SubmitAction) {
            const submitData = (action.data || {}) as {
                rainbow?: {
                    type: string;
                    value?: unknown;
                    text?: string;
                };
                [key: string]: unknown;
            };
            const rainbowData = submitData.rainbow ?? {
                type: "messageBack",
                value: {},
                text: ""
            };
            submitData.rainbow = rainbowData;
            const messageData: SendMessageData = {
                additionalContent: {
                    type:"rainbow/json",
                    message: JSON.stringify(submitData)
                }
            };
            RainbowSDKService.sendMessageToConversation(mainConversation, rainbowData.text ?? "", messageData);
        } else if (action instanceof OpenUrlAction) {
            window.open(action.url, "_blank");
        }
    };

    const isLoading = props.appState === props.States.CreatingSession;
    const showChat = [props.States.Initialised, props.States.Authenticated].includes(props.appState);

    const messagesList = useMemo(() => {
        let tmpMessagesList: any[] = [];

        messages.forEach(message => {
            const tmpMessageListLength = tmpMessagesList.length
            const last_message = tmpMessageListLength > 0 && tmpMessagesList[tmpMessageListLength-1]

            if(tmpMessageListLength > 0 && last_message.fromJid === message.from.jid){
                if (last_message.type === 'message'){
                    tmpMessagesList[tmpMessageListLength-1] = {
                        'type': 'cluster',
                        'fromJid': message.from.jid,
                        'cluster': [last_message.message, message]
                    }
                }else{
                    tmpMessagesList[tmpMessageListLength-1].cluster.push(message)
                }
            }else{
                tmpMessagesList.push({
                    'type': 'message',
                    'fromJid': message.from.jid,
                    'message': message
                })
            }
        });

        return tmpMessagesList;
    }, [messages]);

    return (
        <>
            {isLoading && <LoadingScreen message={t("loading_screen_livechat_loading")} />}
            <div id="chat" className="chat-area" hidden={isLoading || !showChat} ref={chatRef}>
                <div id="chat-listOfMessages" className="chat-messages">
                    {messagesList.map((msg, idx) => {
                        if (msg.type === 'message') {
                            return <Message key={idx} message={msg.message}
                                            adaptiveCardActionsHandler={handleAdaptiveCardActions}/>
                        }
                        else {
                            // @ts-ignore
                            return <Message key={idx} cluster={msg.cluster} adaptiveCardActionsHandler={handleAdaptiveCardActions}/>
                        }
                    })}
                </div>
            </div>
            {typing && <Typing />}
            {!isLoading && (
                props.appState === props.States.Initialised ? (
                    <SendMessageBar bubble={mainConversation} disabled={disabled} />
                ) : props.appState === props.States.Authenticated ? (
                    <SendMessageBar bubble={mainConversation} />
                ) : [
                    props.States.OutOfBusinessHours,
                    props.States.CreatingSession,
                    props.States.Unavailability
                ].includes(props.appState) ? (
                    <SendMessageBar disabled={disabled} />
                ) : props.appState === props.States.ChatArchived ? (
                    <Information message={t("chat_conversation_closed_message") as string} />
                ) : props.appState === props.States.Expired ? (
                    <Information message={t("chat_expired") as string} />
                ) : props.appState === props.States.BadToken ? (
                    <Information message={t("bad_token") as string} />
                ) : props.appState === props.States.Error ? (
                    <Information message={t("unknown_error") as string } />
                ) : null
            )}
        </>
    );
};

export default WelcomeChat;